import { defineMessages } from 'react-intl-next';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

const i18nBase = defineMessages({
	manifestDescription: {
		id: 'confluence-databases-extension.description',
		defaultMessage: 'Create an empty database table',
		description: 'Description for database editor extension.',
	},
	manifestTitle: {
		id: 'confluence-databases-extension.title',
		defaultMessage: 'Database extension',
		description: 'Title for database editor extension.',
	},
	createDatabaseTitle: {
		id: 'confluence-databases-extension.create-database.title',
		defaultMessage: 'New database',
		description: 'Title for create database quick insert action.',
	},
	createDatabaseDescription: {
		id: 'confluence-databases-extension.create-database.description',
		defaultMessage: 'Create an empty database table',
		description: 'Description for create database quick insert action.',
	},
	referenceDatabaseTitle: {
		id: 'confluence-databases-extension.reference-database.title',
		defaultMessage: 'Embed database',
		description: 'Title for reference database quick insert action.',
	},
	referenceDatabaseDescription: {
		id: 'confluence-databases-extension.reference-database.description',
		defaultMessage: 'Display an existing database, entry, or value',
		description: 'Description for reference database quick insert action.',
	},
	referenceDatabaseModalLabel: {
		id: 'confluence-databases-extension.reference-database.modal-label',
		defaultMessage: 'Reference database modal',
		description: 'Label for reference database modal.',
	},
	createDatabaseIconAltText: {
		id: 'confluence-databases-extension.create-database.icon-alt-text',
		defaultMessage: 'Create database icon',
		description: 'Alt text for create database quick insert action icon.',
	},
	referenceDatabaseIconAltText: {
		id: 'confluence-databases-extension.reference-database.icon-alt-text',
		defaultMessage: 'Reference database icon',
		description: 'Alt text for reference database quick insert action icon.',
	},
	successFlagTitle: {
		id: 'confluence-databases-extension.success-flag.title',
		defaultMessage: 'Database added under this content ',
		description: 'Title for success flag when database is created inline.',
	},
	successFlagAction: {
		id: 'confluence-databases-extension.success-flag.action',
		defaultMessage: 'Open database in new tab',
		description: 'Action for success flag when database is created inline.',
	},
	successFlagDescription: {
		id: 'confluence-databases-extension.success-flag.description',
		defaultMessage: 'To view this database in full and edit it with ease, open it in a new tab.',
		description: 'Description for success flag when database is created inline.',
	},
	defaultErrorFlagTitle: {
		id: 'confluence-databases-extension.error-flag.title',
		defaultMessage: 'We’re unable to create right now',
		description: 'Title for default flag when database is created inline.',
	},
	defaultErrorFlagDescription: {
		id: 'confluence-databases-extension.error-flag.description',
		defaultMessage:
			'Refresh or wait a few minutes and try again. If this keeps happening, contact support.',
		description: 'Description for success flag when database is created inline.',
	},
	parentPageMovedErrorTitle: {
		id: 'confluence-databases-extension.error.parent-page-moved-error-title',
		defaultMessage: 'The parent has moved',
		description:
			'Title for error flag when the page is moved when trying to create a database inline.',
	},
	parentPageMovedErrorDescription: {
		id: 'confluence-databases-extension.error.parent-page-moved-error-description',
		defaultMessage: 'Refresh to create in the new location.',
		description:
			'Description for error flag when the page is moved when trying to create a database inline.',
	},
	permissionRemovedErrorTitle: {
		id: 'confluence-databases-extension.error.permission-removed-error-title',
		defaultMessage: 'We’re unable to create this content',
		description:
			'Title for error flag when the user encounters a permissions issue while creating content or tries to create content in a nonexistent space',
	},
	permissionRemovedErrorDescription: {
		id: 'confluence-databases-extension.error.permission-removed-error-description',
		defaultMessage:
			'You don’t have permission to create here or the space has been recently deleted.',
		description:
			'Description for error flag when the user encounters a permissions issue while creating content or tries to create content in a nonexistent space.',
	},
	invalidParentErrorTitle: {
		id: 'confluence-databases-extension.error.invalid-parent-error-title',
		defaultMessage: 'We can’t create a database',
		description:
			'Title for error flag when the page is deleted or archived when trying to create a database inline.',
	},
	invalidParentErrorDescription: {
		id: 'confluence-databases-extension.error.invalid-parent-error-description',
		defaultMessage:
			'This page was deleted or archived. You can create a database on a different page.',
		description:
			'Description for error flag when when the page is deleted or archived when trying to create a database inline.',
	},
	databaseBoardLayoutTitle: {
		id: 'confluence-databases-extension.database-board-layout.title',
		defaultMessage: 'Board layout (Database)',
		description: 'Title for database board layout action.',
	},
	databaseBoardLayoutDescription: {
		id: 'confluence-databases-extension.database-board-layout.description',
		defaultMessage: 'Structure data in a kanban board ',
		description: 'Description for database board layout quick insert action.',
	},
	databaseBoardLayoutIconAltText: {
		id: 'confluence-databases-extension.database-board-layout.icon-alt-text',
		defaultMessage: 'Database Board icon',
		description: 'Alt text for database board layout action icon.',
	},
	databaseGalleryLayoutTitle: {
		id: 'confluence-databases-extension.database-gallery-layout.title',
		defaultMessage: 'Gallery layout (Database)',
		description: 'Title for database gallery layout action.',
	},
	databaseGalleryLayoutDescription: {
		id: 'confluence-databases-extension.database-gallery-layout.description',
		defaultMessage: 'Structure data in a gallery of cards',
		description: 'Description for database gallery layout quick insert action.',
	},
	databaseGalleryLayoutIconAltText: {
		id: 'confluence-databases-extension.database-gallery-layout.icon-alt-text',
		defaultMessage: 'Database gallery icon',
		description: 'Alt text for database gallery layout action icon.',
	},
	databaseTableLayoutTitle: {
		id: 'confluence-databases-extension.database-table-layout.title',
		defaultMessage: 'Table layout (Database)',
		description: 'Title for database table layout action.',
	},
	databaseTableLayoutDescription: {
		id: 'confluence-databases-extension.database-table-layout.description',
		defaultMessage: 'Structure data in a dynamic table',
		description: 'Description for database table layout quick insert action.',
	},
	databaseTableLayoutIconAltText: {
		id: 'confluence-databases-extension.database-table-layout.icon-alt-text',
		defaultMessage: 'Database table icon',
		description: 'Alt text for database table layout action icon.',
	},
});

const i18nLiveDocs = defineMessages({
	invalidParentErrorDescription: {
		id: 'confluence-databases-extension.error.invalid-parent-error-description.livedocs',
		defaultMessage:
			'This item was deleted or archived. You can create a database on a different page or live doc.',
		description:
			'Updated version of confluence-databases-extension.error.invalid-parent-error-description',
	},
});

export const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);
